import React, { useState, useEffect, useRef, useCallback } from "react";
import { apiGet, apiPost } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./ClinicalNotesConsole.css";

interface ClinicalNotesData {
    complaints?: string;
    habits?: string;
    observation?: string;
    pre_op_notes?: string;
    post_op_notes?: string;
}

interface ClinicalNotesConsoleProps {
    visitId: string | number;
    patientId: string | number;
    isLocked?: boolean;
}

// Common templates for quick selection
const COMPLAINT_TEMPLATES = [
    "Pain in tooth",
    "Sensitivity to hot/cold",
    "Bleeding gums",
    "Toothache",
    "Swelling",
    "Difficulty chewing",
    "Bad breath",
    "Jaw pain",
    "Loose tooth",
    "Food impaction",
    "Discoloration",
    "Fractured tooth",
    "Gum recession"
];

const HABIT_TEMPLATES = [
    "Smoking",
    "Bruxism (teeth grinding)",
    "Nail biting",
    "Mouth breathing",
    "Thumb sucking",
    "Tobacco chewing",
    "Alcohol consumption",
    "Clenching",
    "Pen/pencil biting",
    "Ice chewing",
    "None"
];

const OBSERVATION_TEMPLATES = [
    "Caries present",
    "Gingival inflammation",
    "Plaque buildup",
    "Calculus present",
    "Normal oral hygiene",
    "Periodontal disease",
    "Tooth mobility",
    "Abscess formation",
    "Malocclusion",
    "Attrition",
    "Erosion",
    "Staining",
    "Halitosis",
    "Dry mouth"
];

export default function ClinicalNotesConsole({ visitId, patientId, isLocked = false }: ClinicalNotesConsoleProps) {
    const [notes, setNotes] = useState<ClinicalNotesData>({
        complaints: "",
        habits: "",
        observation: "",
        pre_op_notes: "",
        post_op_notes: ""
    });
    const [dmft, setDmft] = useState<{ D: number; M: number; F: number; T: number } | null>(null);
    const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const [lastSavedTime, setLastSavedTime] = useState<string | null>(null);
    const { showToast } = useToast();

    const notesRef = useRef<ClinicalNotesData>(notes);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Load initial notes
    useEffect(() => {
        if (!visitId) return;
        const loadNotes = async () => {
            try {
                const res = await apiGet(`/api/doctor/visit-notes/${visitId}`);
                const data = res as { complaints?: string; habits?: string; observation?: string; pre_op_notes?: string; post_op_notes?: string };
                const loadedNotes = {
                    complaints: data.complaints || "",
                    habits: data.habits || "",
                    observation: data.observation || "",
                    pre_op_notes: data.pre_op_notes || "",
                    post_op_notes: data.post_op_notes || ""
                };
                setNotes(loadedNotes);
                notesRef.current = loadedNotes;
            } catch (err) {
                console.error("Failed to load clinical notes", err);
            }
        };
        loadNotes();
    }, [visitId]);

    useEffect(() => {
        if (!visitId) return;
        apiGet(`/api/doctor/dmft/${visitId}`)
            .then((data) => {
                const d = data as { D?: number; M?: number; F?: number; T?: number };
                setDmft({
                    D: Number(d.D || 0),
                    M: Number(d.M || 0),
                    F: Number(d.F || 0),
                    T: Number(d.T || 0)
                });
            })
            .catch(() => setDmft(null));
    }, [visitId]);

    const saveToBackend = useCallback(async (currentNotes: ClinicalNotesData) => {
        if (isLocked || !visitId) return;
        setSaveStatus("saving");
        try {
            await apiPost("/api/doctor/visit-notes", {
                visit_id: visitId,
                patient_id: patientId,
                ...currentNotes
            });
            setSaveStatus("saved");
            setLastSavedTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
            notesRef.current = currentNotes;
            setTimeout(() => setSaveStatus("idle"), 3000);
        } catch (e) {
            console.error("Auto-save failed", e);
            setSaveStatus("error");
            showToast({ message: "Failed to auto-save clinical notes.", type: "error" });
        }
    }, [visitId, patientId, isLocked, showToast]);

    useEffect(() => {
        const hasChanges =
            notes.complaints !== notesRef.current.complaints ||
            notes.habits !== notesRef.current.habits ||
            notes.observation !== notesRef.current.observation ||
            notes.pre_op_notes !== notesRef.current.pre_op_notes ||
            notes.post_op_notes !== notesRef.current.post_op_notes;

        if (!hasChanges) {
            // If we just loaded, update ref to match state without saving
            // Wait, no. If we just loaded, state == ref.
            // If user types, state updates.
            // Then we check if state != ref.
            return;
        }

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => saveToBackend(notes), 2000);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [notes, saveToBackend]);

    const handleChange = (field: keyof ClinicalNotesData, value: string) => {
        setNotes(prev => ({ ...prev, [field]: value }));
    };

    const handleTemplateSelect = (field: keyof ClinicalNotesData, template: string) => {
        const currentValue = notes[field] || "";
        const newValue = currentValue ? `${currentValue}, ${template}` : template;
        setNotes(prev => ({ ...prev, [field]: newValue }));
    };

    return (
        <div className="clinical-notes-console">
            <div className="console-header">
                <h3>📋 Clinical Notes</h3>
                <div className="console-header-actions">
                    {dmft && (
                        <div className="dmft-badge" title="Decayed, Missing, Filled Teeth">
                            DMFT: D{dmft.D} / M{dmft.M} / F{dmft.F} • T{dmft.T}
                        </div>
                    )}
                    <div className={`save-indicator ${saveStatus}`}>
                        {saveStatus === "saving" && <span>⏳ Saving...</span>}
                        {saveStatus === "saved" && <span>✅ Saved {lastSavedTime}</span>}
                        {saveStatus === "error" && <span>❌ Error saving</span>}
                    </div>
                </div>
            </div>

            <div className="notes-grid">
                {/* Complaints Section */}
                <div className="note-section complaints">
                    <div className="section-label">
                        <span className="icon">🔴</span> Complaints
                    </div>
                    <div className="template-buttons">
                        {COMPLAINT_TEMPLATES.map(template => (
                            <Button
                                key={template}
                                variant="outline"
                                size="sm"
                                onClick={() => handleTemplateSelect("complaints", template)}
                                disabled={isLocked}
                                title={`Add: ${template}`}
                                className="template-btn"
                            >
                                {template}
                            </Button>
                        ))}
                    </div>
                    <textarea
                        value={notes.complaints}
                        onChange={(e) => handleChange("complaints", e.target.value)}
                        disabled={isLocked}
                        placeholder="Patient's chief complaints..."
                    />
                </div>

                {/* Habits Section */}
                <div className="note-section habits">
                    <div className="section-label">
                        <span className="icon">🟠</span> Habits
                    </div>
                    <div className="template-buttons">
                        {HABIT_TEMPLATES.map(template => (
                            <Button
                                key={template}
                                variant="outline"
                                size="sm"
                                onClick={() => handleTemplateSelect("habits", template)}
                                disabled={isLocked}
                                title={`Add: ${template}`}
                                className="template-btn"
                            >
                                {template}
                            </Button>
                        ))}
                    </div>
                    <textarea
                        value={notes.habits}
                        onChange={(e) => handleChange("habits", e.target.value)}
                        disabled={isLocked}
                        placeholder="Smoking, bruxism, etc..."
                    />
                </div>

                {/* Observation Section */}
                <div className="note-section observation">
                    <div className="section-label">
                        <span className="icon">🔵</span> Observation
                    </div>
                    <div className="template-buttons">
                        {OBSERVATION_TEMPLATES.map(template => (
                            <Button
                                key={template}
                                variant="outline"
                                size="sm"
                                onClick={() => handleTemplateSelect("observation", template)}
                                disabled={isLocked}
                                title={`Add: ${template}`}
                                className="template-btn"
                            >
                                {template}
                            </Button>
                        ))}
                    </div>
                    <textarea
                        value={notes.observation}
                        onChange={(e) => handleChange("observation", e.target.value)}
                        disabled={isLocked}
                        placeholder="Clinical findings..."
                    />
                </div>

                {/* Pre-Op Notes */}
                <div className="note-section preop">
                    <div className="section-label">
                        <span className="icon">🧪</span> Pre-Op Notes
                    </div>
                    <textarea
                        value={notes.pre_op_notes}
                        onChange={(e) => handleChange("pre_op_notes", e.target.value)}
                        disabled={isLocked}
                        placeholder="Pre-operative notes, consent, allergies, vitals..."
                    />
                </div>

                {/* Post-Op Notes */}
                <div className="note-section postop">
                    <div className="section-label">
                        <span className="icon">🩹</span> Post-Op Notes
                    </div>
                    <textarea
                        value={notes.post_op_notes}
                        onChange={(e) => handleChange("post_op_notes", e.target.value)}
                        disabled={isLocked}
                        placeholder="Post-operative instructions, medications, follow-up..."
                    />
                </div>
            </div>
        </div>
    );
}
